import { PDFDownloadLink } from '@react-pdf/renderer';
import { EMPTY_LIST, PDF_FILENAME } from 'constants/general';
import { t } from 'services/i18n.service';
import { useAppSelectorShallow } from 'store/hooks';
import { getPackIds } from 'utils/helpers';
import { HabitPackFormat } from 'enums';
import HabitPackActionButton from 'components/common/buttons/HabitPackActionButton';
import PackMarketplaceControls from 'components/common/buttons/PackMarketplaceControls';
import DetailsPageActionsWrapper from '../../dashboard/wrappers/DetailsPageActionsWrapper';
import { useState } from 'react';
import HabitPackPdfDocument from '../../dashboard/marketplace/habitPackPdf/HabitPdfDocument';
import {
  habitPackActionsSelector,
  habitPackAuthenticatedActionsSelector
} from 'store/reducer/habit-pack/selectors';
import { ATTRIB } from 'constants/test';
import {
  isCurrentPath,
  isPublicRoute,
  isRouteEmbedded
} from 'utils/routes.util';
import { ROUTES } from 'constants/routes';
import { canManageMarketplaceEntity } from 'utils/marketplace.util';

const AuthenticatedActions = () => {
  const { currentViewedPack, installedHabitPacks, userId, isUserAdmin } =
    useAppSelectorShallow(habitPackAuthenticatedActionsSelector);

  const isOwner = userId === currentViewedPack?.user_id;
  const canManage = canManageMarketplaceEntity(isOwner, isUserAdmin);
  const showOutsideSettings = !isCurrentPath(ROUTES.SETTINGS);

  const installedPacksIds = getPackIds(installedHabitPacks);
  const userHasPackInstalled =
    currentViewedPack?.id && installedPacksIds.includes(currentViewedPack?.id);

  const isPlayingStandaloneRoutineAllowed =
    userHasPackInstalled &&
    currentViewedPack?.pack_type === HabitPackFormat.STANDALONE;
  const [hasDownloadedPdf, setHasDownloadedPdf] = useState(false);

  const pdfButtonText = (loading: boolean, hasDownloadedPdf: boolean) => {
    if (loading) {
      return t('loading_document');
    } else if (hasDownloadedPdf) {
      return t('downloaded');
    } else {
      return t('download_pdf');
    }
  };

  return (
    <>
      {showOutsideSettings && canManage && (
        <>
          <HabitPackActionButton
            text={t('market.edit')}
            data-testid={ATTRIB.DATA_TESTID.HABIT_PACK_ACTION_EDIT}
          />
          <HabitPackActionButton
            text={t('market.delete')}
            data-testid={ATTRIB.DATA_TESTID.HABIT_PACK_ACTION_DELETE}
          />
        </>
      )}
      {showOutsideSettings && isOwner && (
        <HabitPackActionButton
          text={t('market.duplicate')}
          data-testid={ATTRIB.DATA_TESTID.HABIT_PACK_ACTION_DUPLICATE}
        />
      )}
      <PDFDownloadLink
        className='md:hidden ~text-xs/base font-semibold ~px-8/10 py-2 bg-blue-700 text-white rounded-md'
        onClick={() => setHasDownloadedPdf(true)}
        document={<HabitPackPdfDocument habitPack={currentViewedPack} />}
        fileName={PDF_FILENAME.SETTINGS}>
        {({ loading }) => <>{pdfButtonText(loading, hasDownloadedPdf)}</>}
      </PDFDownloadLink>
      <HabitPackActionButton
        text={t('market.generate_pdf')}
        data-testid={ATTRIB.DATA_TESTID.HABIT_PACK_ACTION_GENERATE_PDF}
      />
      {isPlayingStandaloneRoutineAllowed && (
        <HabitPackActionButton
          text={t('market.play')}
          data-testid={ATTRIB.DATA_TESTID.HABIT_PACK_ACTION_PLAY}
        />
      )}
      {showOutsideSettings && isOwner && currentViewedPack && (
        <PackMarketplaceControls habitPack={currentViewedPack} />
      )}
    </>
  );
};

export default function HabitPackActions() {
  const { currentViewedPack, installedHabitPacks } = useAppSelectorShallow(
    habitPackActionsSelector
  );

  const installedPacksIds = getPackIds(installedHabitPacks);
  const userHasPackInstalled =
    currentViewedPack?.id && installedPacksIds.includes(currentViewedPack?.id);

  const isFocusOnlyHabitPack =
    [
      ...(currentViewedPack?.morning_activities ?? []),
      ...(currentViewedPack?.break_activities ?? []),
      ...(currentViewedPack?.evening_activities ?? [])
    ].length === EMPTY_LIST;

  return (
    <DetailsPageActionsWrapper>
      <HabitPackActionButton
        text={t('back')}
        data-testid={ATTRIB.DATA_TESTID.HABIT_PACK_ACTION_BACK}
      />
      {!isPublicRoute() && (
        <>
          {!isFocusOnlyHabitPack &&
            (userHasPackInstalled ? (
              <HabitPackActionButton
                text={t('market.uninstall')}
                data-testid={ATTRIB.DATA_TESTID.HABIT_PACK_ACTION_UNINSTALL}
              />
            ) : (
              <HabitPackActionButton
                text={t('market.install')}
                data-testid={ATTRIB.DATA_TESTID.HABIT_PACK_ACTION_INSTALL}
              />
            ))}
          {/* AuthenticatedActions (edit/delete/duplicate/PDF/play) stays gated by
              isRouteEmbedded even though install/uninstall is now shown in embedded context.
              Reason: in the embedded Marketplace WebView the host app handles those
              actions itself; surfacing them here would duplicate UI controls and
              interfere with the host's native state management. */}
          {!isRouteEmbedded && !isCurrentPath(ROUTES.SETTINGS) && (
            <AuthenticatedActions />
          )}
        </>
      )}
    </DetailsPageActionsWrapper>
  );
}
